import { getSession } from '../auth';
import { toast } from '../components/toast';
import { isProductionData, listLeads, updateLeadStatus, createLead } from '../lib/repository';
import { openModal, closeModal } from '../components/modal';
import type { LeadRecord, LeadStatus } from '../domain';

const STAGES = ['new', 'contacted', 'qualified', 'won'] as const;
const STAGE_HEAD_CLASS: Record<string, string> = {
  new: 'pipe-col-head-new', contacted: 'pipe-col-head-contacted', qualified: 'pipe-col-head-qualified', won: 'pipe-col-head-won',
};
let productionLeads: LeadRecord[] | null = null;
let loading = false;

function load(): void {
  if (!isProductionData() || productionLeads || loading) return;
  loading = true;
  listLeads().then((r) => { productionLeads = r; loading = false; (window as any).__rerender?.(); }).catch(() => { loading = false; });
}

export function renderPipeline(): string {
  load();
  const leads = productionLeads || [];
  const session = getSession();
  const canAdd = !!session && ['owner', 'admin', 'teamLead', 'salesAssociate'].includes(session.role);
  const cols = STAGES.map((st) => {
    const items = leads.filter((l) => l.status === st).map((l) => (
      '<div class="pipe-card" data-lead-id="' + l.id + '">' +
        '<div class="row"><div class="avatar av-c1">' + (l.name || '?').slice(0, 2).toUpperCase() + '</div>' +
        '<div style="flex:1;min-width:0"><div class="t-strong" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + (l.name || '—') + '</div>' +
        '<div class="t-meta t-mono" style="font-size:11px">' + (l.phone || '') + '</div></div></div>' +
        '<div class="row" style="margin-top:8px;gap:6px;flex-wrap:wrap"><span class="pill pill-info">' + (l.courseId || 'General') + '</span>' +
        '<span class="spacer"></span><span class="t-meta" style="font-size:10px;text-transform:uppercase">' + (l.source || 'manual') + '</span></div>' +
      '</div>'
    )).join('');
    const head = '<div class="pipe-head ' + STAGE_HEAD_CLASS[st] + '"><span class="dot" style="background:currentColor"></span>' + st.charAt(0).toUpperCase() + st.slice(1) + '<span class="spacer"></span><span class="t-mono" style="opacity:.7">' + items.length + '</span></div>';
    return '<div class="pipe-col">' + head + '<div class="stack-tight">' + (items || '<div class="t-meta" style="padding:10px 6px;text-align:center">—</div>') + '</div></div>';
  }).join('');
  return (
    '<div class="screen-head"><div><div class="t-eyebrow">Leads · pipeline</div><h1 class="t-h1">Pipeline</h1>' +
    '<p class="t-meta">' + (loading ? 'loading…' : leads.length + ' total leads across stages') + '</p></div>' +
    (canAdd ? '<button class="btn btn-accent touch-target" data-action="pipeline-add">+ Add lead</button>' : '') + '</div>' +
    '<div class="pipeline stagger">' + cols + '</div>'
  );
}

export function bindPipeline(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-action="pipeline-add"]')) { openAddLead(); return; }
    const card = t.closest<HTMLElement>('[data-lead-id]');
    if (!card || !card.dataset.leadId) return;
    const order: string[] = ['new', 'contacted', 'qualified', 'won'];
    const cur = (productionLeads || []).find((l) => l.id === card.dataset.leadId)?.status || 'new';
    const curIdx = Math.max(0, order.indexOf(cur));
    const nextStatus = order[Math.min(curIdx + 1, order.length - 1)] as LeadStatus;
    void updateLeadStatus(card.dataset.leadId, nextStatus)
      .then(() => { toast('Moved to ' + nextStatus, 'success'); (window as any).__rerender?.(); })
      .catch((err) => toast(err instanceof Error ? err.message : 'Move failed', 'warn'));
  });
}

function openAddLead(): void {
  openModal({
    id: 'lead-add',
    title: 'Add lead',
    subtitle: 'A new prospect enters the pipeline at stage: new.',
    width: 480,
    html: '<form id="lead-add-form"><div class="stack-tight">' +
      '<label class="t-eyebrow">Name<input required name="name" placeholder="e.g. Priya Sharma" class="form-input"/></label>' +
      '<label class="t-eyebrow">Phone<input name="phone" placeholder="+91 98XXX XXXXX" class="form-input"/></label>' +
      '<label class="t-eyebrow">Course / interest<input name="courseId" placeholder="e.g. Entrepreneurship Incubator" class="form-input"/></label>' +
      '<label class="t-eyebrow">Source<select name="source" class="form-input"><option value="manual">Manual</option><option value="website">Website apply form</option><option value="referral">Referral</option><option value="instagram">Instagram</option></select></label>' +
      '</div><div class="row" style="margin-top:24px;gap:10px;justify-content:flex-end">' +
      '<button class="btn btn-ghost" type="button" data-lead-close>Cancel</button>' +
      '<button class="btn btn-primary" type="submit">Add lead</button></div></form>',
  }, (panel) => {
    panel.querySelectorAll('[data-lead-close]').forEach((b) => b.addEventListener('click', () => closeModal('lead-add')));
  });
  document.addEventListener('submit', leadSubmit, { once: true });
}

async function leadSubmit(e: Event): Promise<void> {
  const form = (e.target as HTMLElement).closest<HTMLFormElement>('#lead-add-form');
  if (!form) return;
  e.preventDefault();
  const fd = new FormData(form);
  const payload = {
    name: String(fd.get('name') || '').trim(),
    phone: String(fd.get('phone') || '').trim(),
    courseId: String(fd.get('courseId') || '').trim(),
    source: String(fd.get('source') || 'manual'),
    status: 'new' as LeadStatus,
  };
  if (!payload.name) { toast('Name is required', 'warn'); return; }
  const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]'); if (btn) btn.disabled = true;
  try {
    await createLead(payload);
    productionLeads = null;
    toast('Lead added to pipeline', 'success');
    closeModal('lead-add');
    (window as any).__rerender?.();
  } catch (err) {
    toast(err instanceof Error ? err.message : 'Could not add lead', 'warn');
    if (btn) btn.disabled = false;
  }
}
